import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Outlet, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material';
import _merge from 'lodash/merge';
import _omit from 'lodash/omit';
import _pick from 'lodash/pick';
import { StringParam } from 'serialize-query-params';
import { useQueryParams } from 'use-query-params';
import { Schema } from 'yup';

import {
  FolderType,
  getGetSuborganizationNormsQueryKey,
  OrganizationNormsResponse,
  ProcessSettings,
  SuborganizationDTO,
  TalentQuestionsResponseDTO,
  useGetOrganizationTalentQuestions,
  useGetSuborganization,
  useGetSuborganizationNorms,
  useUpdateSuborganization,
  useUpdateSuborganizationNorms
} from '@/api/generated';
import BackdropLoader from '@/components/BackdropLoader';
import ErrorGeneric from '@/components/ErrorGeneric/ErrorGeneric';
import { deserializeNorms, serializeNorms } from '@/containers/OrganizationSettings/norms';
import { OrganizationSettingsFieldValues } from '@/containers/OrganizationSettings/types';
import { LocationProvider } from '@/hooks/entities/useLocation';
import { useSuborganizationPermissions } from '@/hooks/permissions/useSuborganizationPermissions';
import {
  PROCESS_SETTINGS_STEP_KEYS,
  ROUTER_PATHS,
  SETTINGS_STEP_KEYS,
  suborgItemSettingsTabs,
  toOrganizationItemSettings
} from '@/root/linker';
import {
  accessInvitationMappingPresave,
  createAccessInvitationDefaultValues,
  createTalentRegistrationDefaultValues,
  createTestPropertiesDefaultValues,
  testSettingsMappingPresave
} from '@/views/ProcessSettingsForm/defaults';
import SettingsShell from '@/views/SettingsShell/SettingsShell';

import {
  getNormsValidationSchema,
  getProcessSettingsValidationSchema,
  getRolesMembersValidationSchema,
  getSubOrgBasicInfoValidationSchema
} from './schema';
import { type SubOrganizationData, type SubOrganizationFieldValues, SubOrganizationUpdateRequest } from './types';

const DEFAULT_ORGANIZATION_VALUES: Partial<OrganizationSettingsFieldValues> = {
  testValidityDays: 180
};

const SUB_ORGANIZATION_KEYS_BY_TAB = {
  [SETTINGS_STEP_KEYS.BASIC_INFO]: [
    'status',
    'name',
    'legalName',
    'industry',
    'offices',
    'countries',
    'keyAccountManagers',
    'employeeCount',
    'accountNumbers',
    'numberOfEmployees',
    'jiraId',
    'legacyId',
    'pipedriveId'
  ],
  [SETTINGS_STEP_KEYS.ROLES_AND_MEMBERS]: ['admins', 'organizationAdmins', 'userInviteEmailLanguage'],
  [SETTINGS_STEP_KEYS.PROCESS_SETTINGS]: ['processSettings'],
  [SETTINGS_STEP_KEYS.NORMS]: ['testValidityDays', 'norms']
} as { [key: string]: string[] };

const ALL_SUB_ORGANIZATION_KEYS = Object.values(SUB_ORGANIZATION_KEYS_BY_TAB).flatMap(list => list);
const OMIT_ON_SAVE_KEYS = ['status', 'norms'];

const TRANSFORMED_ON_LOAD_KEYS = ['admins', 'industry', 'keyAccountManagers', 'processSettings', 'offices', 'norms'];

const presaveTransform = (fieldValues: SubOrganizationFieldValues, stepKey: SETTINGS_STEP_KEYS) => {
  const processSettings = _merge(
    accessInvitationMappingPresave(fieldValues[PROCESS_SETTINGS_STEP_KEYS.SETTINGS_ACCESS_INVITATION]),
    testSettingsMappingPresave(fieldValues[PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TEST_PROPERTIES])
  ) satisfies ProcessSettings;

  const updateRequest = _pick(fieldValues, ALL_SUB_ORGANIZATION_KEYS) as SubOrganizationUpdateRequest;
  // FIXME: Uncomment this when backend is updated.
  // & { admins?: { email?: string, id?: string }[]; };

  updateRequest.processSettings = processSettings;
  // FIXME: Uncomment this when backend is updated.
  // updateRequest.admins =
  //   fieldValues?.admins?.map(admin => (admin.includes('@') ? { email: admin } : { id: admin })) || [];

  return _omit(_pick(updateRequest, SUB_ORGANIZATION_KEYS_BY_TAB[stepKey]), OMIT_ON_SAVE_KEYS);
};

const preloadTransform = (
  subOrganization: SubOrganizationData,
  suborganizationNormsData: OrganizationNormsResponse | undefined,
  talentQuestions: TalentQuestionsResponseDTO
) => {
  const accessInvitationData = createAccessInvitationDefaultValues(subOrganization.processSettings!);
  const testPropertiesData = createTestPropertiesDefaultValues(subOrganization.processSettings!);

  const fieldValues = _omit(
    _pick(subOrganization, ALL_SUB_ORGANIZATION_KEYS),
    TRANSFORMED_ON_LOAD_KEYS
  ) as SubOrganizationFieldValues;

  fieldValues.industry = subOrganization?.industry?.id;
  fieldValues.keyAccountManagers = subOrganization?.keyAccountManagers?.filter(user => user?.id)?.map(user => user.id!);
  fieldValues.admins = subOrganization?.admins?.filter(admin => admin?.id)?.map(({ id }) => ({ id: id! }));
  fieldValues.offices = subOrganization?.offices;
  fieldValues[PROCESS_SETTINGS_STEP_KEYS.SETTINGS_ACCESS_INVITATION] = accessInvitationData;
  fieldValues[PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TEST_PROPERTIES] = testPropertiesData;
  fieldValues.testValidityDays = fieldValues?.testValidityDays || DEFAULT_ORGANIZATION_VALUES?.testValidityDays;

  const deserializedNorms = deserializeNorms(suborganizationNormsData);
  if (deserializedNorms) {
    fieldValues.norms = deserializedNorms;
  }

  fieldValues[PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TALENT_REGISTRATION] =
    createTalentRegistrationDefaultValues(talentQuestions);

  return fieldValues;
};

const URL_CONFIG_MAP = {
  locationSlug: StringParam
};

const SubOrganizationSettingsInner: FC = () => {
  const { spacing } = useTheme();
  const { t } = useTranslation(['settings', 'common', 'validation']);
  const { organizationSlug } = useParams() as { organizationSlug: string; locationSlug: string };
  const [{ locationSlug }] = useQueryParams(URL_CONFIG_MAP);

  const suborganizationPermissionVariables = useMemo(
    () => ({
      organizationSlug,
      locationSlug: locationSlug ?? undefined,
      locationType: FolderType.SUB_ORGANIZATION
    }),
    [locationSlug, organizationSlug]
  );

  const {
    value: { canViewProcessSettings, canViewIdentificationAndNorms, canViewBasicInfo, canViewRolesAndUsers } = {},
    isLoading: isPermissionLoading
  } = useSuborganizationPermissions({
    variables: suborganizationPermissionVariables
  });
  const {
    data: subOrganization,
    isLoading: isSubOrganizationLoading,
    isError: isSubOrganizationError,
    refetch: refetchSubOrganization
  } = useGetSuborganization(organizationSlug, locationSlug!);
  const {
    data: suborganizationNormsData,
    isLoading: isSuborganizationNormsLoading,
    isError: isSuborganizationNormsError
  } = useGetSuborganizationNorms(organizationSlug, locationSlug!);
  const { mutateAsync: updateSubOrganization, isLoading: isSubOrganizationUpdating } = useUpdateSuborganization();
  const { mutateAsync: updateSuborganizationNorms } = useUpdateSuborganizationNorms();
  const queryClient = useQueryClient();

  const {
    data: talentQuestions,
    isLoading: isTalentQuestionsLoading,
    isError: isTalentQuestionsError
  } = useGetOrganizationTalentQuestions<TalentQuestionsResponseDTO, object>(organizationSlug);

  const showBackdropLoader =
    isSubOrganizationLoading ||
    isSubOrganizationUpdating ||
    isPermissionLoading ||
    isSuborganizationNormsLoading ||
    isTalentQuestionsLoading;

  const hasBootstrapError = isSubOrganizationError || isSuborganizationNormsError || isTalentQuestionsError;

  const saveSubOrganizationSettings = useCallback(
    async (data: SubOrganizationFieldValues, stepKey: SETTINGS_STEP_KEYS) => {
      const organizationNorms = stepKey === SETTINGS_STEP_KEYS.NORMS && data.norms ? serializeNorms(data.norms) : null;

      const updatePromises = [
        updateSubOrganization({
          slug: organizationSlug,
          subSlug: locationSlug!,
          data: presaveTransform(data, stepKey)
        }),
        organizationNorms &&
          organizationNorms.length > 0 &&
          updateSuborganizationNorms({
            organizationSlug,
            suborganizationSlug: locationSlug!,
            data: {
              organizationNorms: organizationNorms
            }
          })
      ].filter(Boolean) as Promise<SuborganizationDTO | OrganizationNormsResponse>[];

      await Promise.all(updatePromises);
      await refetchSubOrganization();
      if (organizationNorms) {
        await queryClient.invalidateQueries({
          queryKey: getGetSuborganizationNormsQueryKey(organizationSlug, locationSlug!)
        });
      }
    },
    [
      locationSlug,
      organizationSlug,
      refetchSubOrganization,
      updateSubOrganization,
      updateSuborganizationNorms,
      queryClient
    ]
  );

  const tabs = useMemo(
    () =>
      suborgItemSettingsTabs(
        t,
        (stepKey: SETTINGS_STEP_KEYS) =>
          toOrganizationItemSettings({
            organizationSlug,
            locationSlug: locationSlug!,
            stepKey,
            locationType: FolderType.SUB_ORGANIZATION
          }),
        canViewProcessSettings,
        canViewIdentificationAndNorms,
        canViewBasicInfo,
        canViewRolesAndUsers
      ),
    [
      locationSlug,
      organizationSlug,
      t,
      canViewProcessSettings,
      canViewIdentificationAndNorms,
      canViewBasicInfo,
      canViewRolesAndUsers
    ]
  );

  const defaultValues = useMemo(() => {
    if (!subOrganization || talentQuestions === undefined) return undefined;

    return preloadTransform(subOrganization as SubOrganizationData, suborganizationNormsData, talentQuestions);
  }, [subOrganization, suborganizationNormsData, talentQuestions]);

  const validations = useMemo(
    () =>
      ({
        [SETTINGS_STEP_KEYS.BASIC_INFO]: getSubOrgBasicInfoValidationSchema(t),
        [SETTINGS_STEP_KEYS.ROLES_AND_MEMBERS]: getRolesMembersValidationSchema(t),
        [SETTINGS_STEP_KEYS.PROCESS_SETTINGS]: getProcessSettingsValidationSchema(t),
        [SETTINGS_STEP_KEYS.NORMS]: getNormsValidationSchema(t)
      }) as { [key: string]: Schema<SubOrganizationFieldValues> },
    [t]
  );

  const defaultStepKey = useMemo(() => {
    if (canViewBasicInfo) return SETTINGS_STEP_KEYS.BASIC_INFO;
    if (canViewRolesAndUsers) return SETTINGS_STEP_KEYS.ROLES_AND_MEMBERS;
    if (canViewProcessSettings) return SETTINGS_STEP_KEYS.PROCESS_SETTINGS;
    if (canViewIdentificationAndNorms) return SETTINGS_STEP_KEYS.NORMS;

    return SETTINGS_STEP_KEYS.BASIC_INFO;
  }, [canViewProcessSettings, canViewIdentificationAndNorms, canViewBasicInfo, canViewRolesAndUsers]);

  if (showBackdropLoader) {
    return <BackdropLoader />;
  }

  if (hasBootstrapError) {
    return <ErrorGeneric />;
  }

  return (
    <SettingsShell
      tabs={tabs}
      defaultValues={defaultValues!}
      defaultRoute={toOrganizationItemSettings({
        organizationSlug,
        stepKey: defaultStepKey,
        locationSlug: locationSlug!,
        locationType: FolderType.SUB_ORGANIZATION
      })}
      routePattern={ROUTER_PATHS.SUBORGANIZATION_SETTINGS_GENERIC}
      sx={{ px: 5, pb: 5, margin: spacing(2, 1) }}
      onSave={saveSubOrganizationSettings}
      validations={validations}
    >
      <Outlet />
    </SettingsShell>
  );
};

const SubOrganizationSettings: FC = () => {
  const { organizationSlug } = useParams() as { organizationSlug: string };
  const [{ locationSlug }] = useQueryParams(URL_CONFIG_MAP);

  if (!locationSlug) {
    return null;
  }

  return (
    <LocationProvider
      organizationSlug={organizationSlug}
      locationSlug={locationSlug}
      locationType={FolderType.SUB_ORGANIZATION}
    >
      <SubOrganizationSettingsInner />
    </LocationProvider>
  );
};

export default SubOrganizationSettings;
